import React, { createContext, useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { createEventId } from '../utils/eventId';
import { getStoredPixelContact, identifyTikTokUser } from '../utils/tiktokPixel';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

const createBundleId = () =>
  `bundle-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;

const sanitizeVariantSelections = (value) => {
  if (!value || typeof value !== 'object') return {};

  return Object.entries(value).reduce((acc, [key, val]) => {
    if (typeof key !== 'string') return acc;
    if (typeof val !== 'string') return acc;
    const trimmed = val.trim();
    if (!trimmed) return acc;
    acc[key] = trimmed;
    return acc;
  }, {});
};

const buildVariantKey = (selections) => {
  const entries = Object.entries(selections);
  if (!entries.length) return '';

  return entries
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key.toLowerCase()}:${value.toLowerCase()}`)
    .join('|');
};

const buildVariantLabel = (selections, existingLabel) => {
  if (existingLabel) return existingLabel;

  const entries = Object.entries(selections);
  if (!entries.length) return undefined;

  return entries.map(([key, value]) => `${key}: ${value}`).join(', ');
};

const normalizeCartItem = (item) => {
  const quantity = item.quantity ?? 1;
  const variantSelections = sanitizeVariantSelections(item.variantSelections);
  const variantKey = buildVariantKey(variantSelections);
  const variantLabel = buildVariantLabel(variantSelections, item.variantLabel);
  const offerMeta = {
    ...(item.offerMeta || {}),
  };

  if (variantLabel && !offerMeta.variantLabel) {
    offerMeta.variantLabel = variantLabel;
  }

  if (Object.keys(variantSelections).length && !offerMeta.selectedVariants) {
    offerMeta.selectedVariants = variantSelections;
  }

  return {
    ...item,
    productId: item.productId || item.id,
    quantity: quantity < 1 ? 1 : quantity,
    price: item.price ?? item.originalPrice ?? 0,
    originalPrice: item.originalPrice ?? item.price ?? 0,
    stock:
      typeof item.stock === 'number' && item.stock >= 0 ? item.stock : Number.MAX_SAFE_INTEGER,
    offerType: item.offerType || 'NONE',
    offerApplied: item.offerApplied ?? item.offerType === 'BOGO',
    isFreeItem: Boolean(item.isFreeItem),
    offerLabel: item.offerLabel,
    bundleId: item.bundleId,
    bundleKey: item.bundleKey,
    comboId: item.comboId,
    comboLabel: item.comboLabel,
    offerMeta,
    variantSelections,
    variantKey,
    variantLabel,
    quantityBreak: item.quantityBreak || null, // Preserve quantity break data
  };
};

const enforceBundleQuantity = (items, mainIndex, freeIndex, newQuantity) => {
  const updated = [...items];
  if (mainIndex > -1) {
    updated[mainIndex] = {
      ...updated[mainIndex],
      quantity: newQuantity,
    };
  }
  if (freeIndex > -1) {
    updated[freeIndex] = {
      ...updated[freeIndex],
      quantity: newQuantity,
    };
  }
  return updated;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [buyNowItem, setBuyNowItem] = useState(null);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) setCartItems(JSON.parse(savedCart));
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);


    // Load cart and buyNowItem from localStorage on mount
    useEffect(() => {
      const savedCart = localStorage.getItem('cart');
      const savedBuyNowItem = localStorage.getItem('buyNowItem');
      
      if (savedCart) setCartItems(JSON.parse(savedCart));
      if (savedBuyNowItem) setBuyNowItem(JSON.parse(savedBuyNowItem));
    }, []);
  
    // Save cart and buyNowItem to localStorage whenever they change
    useEffect(() => {
      localStorage.setItem('cart', JSON.stringify(cartItems));
      localStorage.setItem('buyNowItem', JSON.stringify(buyNowItem));
    }, [cartItems, buyNowItem]);
  

  const addToCart = (payload) => {
    const fallbackPrice = payload.price ?? payload.originalPrice ?? 0;

    setBuyNowItem(null);

    if (payload.offerType === 'BOGO' && Array.isArray(payload.bundleItems)) {
      const bundleKey = payload.bundleKey || payload.bundleId || createBundleId();
      const bundleId = payload.bundleId || bundleKey;
      const mainItemRaw =
        payload.bundleItems.find((item) => !item.isFreeItem) || payload.bundleItems[0];
      const freeItemRaw =
        payload.bundleItems.find((item) => item.isFreeItem) ||
        payload.bundleItems[1];

      if (!mainItemRaw || !freeItemRaw) {
        toast.error('Incomplete BOGO bundle. Please try again.');
        return;
      }

      const mainItem = normalizeCartItem({
        ...mainItemRaw,
        quantity: 1,
        offerType: 'BOGO',
        offerLabel: mainItemRaw.offerLabel || 'Main Product',
        bundleId,
        bundleKey,
        comboLabel: payload.comboLabel || mainItemRaw.comboLabel,
        comboId: payload.comboId || mainItemRaw.comboId,
        offerMeta: {
          ...(mainItemRaw.offerMeta || {}),
          trackingKey: payload.bogoTrackingKey,
          analyticsTag: payload.analyticsTag,
        },
      });

      const freeItem = normalizeCartItem({
        ...freeItemRaw,
        quantity: 1,
        offerType: 'BOGO',
        offerLabel: freeItemRaw.offerLabel || 'Free Product',
        bundleId,
        bundleKey,
        isFreeItem: true,
        price: 0,
        comboLabel: payload.comboLabel || freeItemRaw.comboLabel,
        comboId: payload.comboId || freeItemRaw.comboId,
        offerMeta: {
          ...(freeItemRaw.offerMeta || {}),
          trackingKey: payload.bogoTrackingKey,
          analyticsTag: payload.analyticsTag,
        },
      });

      if (mainItem.stock <= 0 || freeItem.stock <= 0) {
        toast.error('Selected combination is out of stock.');
        return;
      }

      // TikTok Pixel: AddToCart event for BOGO bundle
      const bogoContentId = String(mainItem.productId || mainItem.id || '').trim();
      const bogoValue = mainItem.price || mainItem.originalPrice || 0;
      // Shared with the CAPI call below so TikTok counts one AddToCart, not two
      const bogoEventId = createEventId('atc');
      const pixelContact = getStoredPixelContact();
      identifyTikTokUser(pixelContact);
      if (window.ttq && bogoContentId) {
        window.ttq.track("AddToCart", {
          contents: [{
            content_id: bogoContentId,
            content_type: "product",
            content_name: mainItem.title || mainItem.name,
            quantity: 1,
            price: bogoValue,
          }],
          content_type: "product",
          value: bogoValue,
          currency: "PKR"
        }, { event_id: bogoEventId });
      }

      // Meta / TikTok CAPI: AddToCart event for BOGO bundle (fire and forget)
      import('../lib/axios').then(({ API }) => {
        const contentIds = [mainItem.productId || mainItem.id, freeItem.productId || freeItem.id]
          .map((id) => String(id || '').trim())
          .filter(Boolean);
        if (!contentIds.length) return;
        API.post('/orders/events/add-to-cart', {
          value: bogoValue,
          currency: 'PKR',
          eventSourceUrl: window.location.href,
          eventId: bogoEventId,
          contentIds: contentIds,
          numItems: 2,
          contentType: 'product',
          ...(pixelContact.phone ? { phone: pixelContact.phone } : {}),
          ...(pixelContact.email ? { email: pixelContact.email } : {}),
        }).catch(err => console.warn('[Meta CAPI] AddToCart failed:', err));
      }).catch(err => console.warn('[Meta CAPI] Failed to track AddToCart:', err));

      setCartItems((prev) => {
        const existingMainIndex = prev.findIndex(
          (item) => item.bundleKey === bundleKey && !item.isFreeItem
        );
        const existingFreeIndex = prev.findIndex(
          (item) => item.bundleKey === bundleKey && item.isFreeItem
        );

        if (existingMainIndex > -1) {
          const existingMain = prev[existingMainIndex];
          const existingFree =
            existingFreeIndex > -1 ? prev[existingFreeIndex] : null;
          const nextQuantity = existingMain.quantity + 1;

          if (nextQuantity > existingMain.stock) {
            toast.error(
              `Only ${existingMain.stock} bundles available for this combo.`
            );
            return prev;
          }
          if (existingFree && nextQuantity > existingFree.stock) {
            toast.error(
              `Only ${existingFree.stock} bundles available for this combo.`
            );
            return prev;
          }

          let updatedItems = enforceBundleQuantity(
            prev,
            existingMainIndex,
            existingFreeIndex,
            nextQuantity
          );

          if (existingFreeIndex === -1) {
            updatedItems = [
              ...updatedItems,
              {
                ...freeItem,
                quantity: nextQuantity,
              },
            ];
          }

          return updatedItems;
        }

        return [...prev, mainItem, freeItem];
      });

      return;
    }

    const normalizedProduct = normalizeCartItem({
      ...payload,
      price: fallbackPrice,
    });
    
    // Debug: Log quantity break data
    if (normalizedProduct.quantityBreak) {
      console.log('[CartContext] Normalized product with quantity break:', {
        'title': normalizedProduct.title,
        'quantity': normalizedProduct.quantity,
        'price': normalizedProduct.price,
        'quantityBreak': normalizedProduct.quantityBreak,
      });
    }

    if (normalizedProduct.stock <= 0) {
      toast.error('Out of stock');
      return;
    }
  
    setCartItems((prevItems) => {
      const existingItemIndex = prevItems.findIndex(
        (item) =>
          item.productId === normalizedProduct.productId &&
          item.offerType === normalizedProduct.offerType &&
          !item.bundleKey &&
          (item.variantKey || '') === (normalizedProduct.variantKey || '')
      );
      const existingItem = prevItems[existingItemIndex];
  
      if (existingItem) {
        const addQty = Math.max(1, Number(normalizedProduct.quantity) || 1);
        const nextQuantity = existingItem.quantity + addQty;

        if (nextQuantity > normalizedProduct.stock) {
          toast.error(`Only ${normalizedProduct.stock} in stock`);
          return prevItems;
        }
  
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex] = {
          ...existingItem,
          quantity: nextQuantity,
        };
        return updatedItems;
      }

      return [...prevItems, normalizedProduct];
    });

    // TikTok Pixel: AddToCart event for regular product
    const contentId = String(normalizedProduct.productId || normalizedProduct.id || '').trim();
    const atcValue = normalizedProduct.price || normalizedProduct.originalPrice || 0;
    // Shared with the CAPI call below so TikTok counts one AddToCart, not two
    const atcEventId = createEventId('atc');
    const pixelContact = getStoredPixelContact();
    identifyTikTokUser(pixelContact);
    if (window.ttq && contentId) {
      window.ttq.track("AddToCart", {
        contents: [{
          content_id: contentId,
          content_type: "product",
          content_name: normalizedProduct.title || normalizedProduct.name,
          quantity: 1,
          price: atcValue,
        }],
        content_type: "product",
        value: atcValue,
        currency: "PKR"
      }, { event_id: atcEventId });
    }

    // Meta / TikTok CAPI: AddToCart event for regular product (fire and forget)
    import('../lib/axios').then(({ API }) => {
      if (!contentId) return;
      API.post('/orders/events/add-to-cart', {
        value: atcValue,
        currency: 'PKR',
        eventSourceUrl: window.location.href,
        eventId: atcEventId,
        contentIds: [contentId],
        numItems: 1,
        contentType: 'product',
        ...(pixelContact.phone ? { phone: pixelContact.phone } : {}),
        ...(pixelContact.email ? { email: pixelContact.email } : {}),
      }).catch(err => console.warn('[Meta CAPI] AddToCart failed:', err));
    }).catch(err => console.warn('[Meta CAPI] Failed to track AddToCart:', err));
  };
  
  const removeFromCart = (target) => {
    setCartItems((prev) => {
      if (!target) return prev;

      if (typeof target === 'object') {
        if (target.bundleKey) {
          return prev.filter((item) => item.bundleKey !== target.bundleKey);
        }
        if (target.productId) {
        return prev.filter(
          (item) =>
            !(
              item.productId === target.productId &&
              (item.variantKey || '') === (target.variantKey || '') &&
              !item.bundleKey
            )
        );
        }
      }

      return prev.filter((item) => item.productId !== target);
    });
  };
  
  const updateQuantity = (target, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(target);
    } else {
      setCartItems((prev) =>
        prev.map((item) => {
          if (!target) return item;

          const matchesBundle =
            typeof target === 'object' && target.bundleKey
              ? item.bundleKey === target.bundleKey
              : false;

          const matchesProduct =
            typeof target === 'object'
              ? item.productId === target.productId &&
                !target.bundleKey &&
                (item.variantKey || '') === (target.variantKey || '')
              : item.productId === target;

          if (matchesBundle || matchesProduct) {
            if (newQuantity > item.stock) {
              toast.error(`Only ${item.stock} in stock`);
              return item;
            }

            const updatedItem = { ...item, quantity: newQuantity };
            if (item.offerType === 'BOGO') {
              updatedItem.offerApplied = true;
            }
            return updatedItem;
          }
          return item;
        })
      );
    }
  };
  
  

  const clearCart = () => setCartItems([]);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        buyNowItem,
        setBuyNowItem,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
